import _ from "lodash";
import { useEffect, useState } from "react";
import { Box, Typography, useTheme, Chip, CircularProgress, Skeleton, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid2";
import moment from "moment-timezone";
import Paper from "@mui/material/Paper";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import DescriptionIcon from "@mui/icons-material/Description";
import CategoryIcon from "@mui/icons-material/Category";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EventIcon from "@mui/icons-material/Event";
import InventoryIcon from "@mui/icons-material/Inventory";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { getFacilityNameByRadius } from "../../../utils/lat_lon_utils";
import RecursiveTreeView from "../../../components/TreeView/RecursiveTreeView";

function Overview(props) {
  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, mode } = palette;
  const isDark = mode === "dark";
  
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";

  const bgContainer = isDark ? "#053350" : "#ffffff";
  const bgCard = isDark ? "#0a4d6e" : "#ffffff";
  const borderColor = isDark ? "rgba(160, 196, 109, 0.3)" : "#e2e8f0";
  const textPrimary = isDark ? "#ffffff" : "#0f172a";
  const textSecondary = isDark ? "#b8c9da" : "#64748b";

  // Asset Information: same container background as Hierarchy (bgContainer), page text colors
  const assetInfoColors = isDark
    ? {
        containerBg: bgContainer,
        containerBorder: "rgba(148, 163, 184, 0.18)",
        pillBg: "rgba(15, 55, 80, 0.85)",
        pillBorder: "rgba(148, 163, 184, 0.22)",
        pillHoverBg: "rgba(20, 65, 95, 0.95)",
        pillHoverBorder: "rgba(160, 196, 109, 0.4)",
        label: textSecondary,
        value: textPrimary,
        icon: greenColor,
      }
    : {
        containerBg: "#ffffff",
        containerBorder: "#e2e8f0",
        pillBg: "#f8fafc",
        pillBorder: "#e2e8f0",
        pillHoverBg: "#f1f5f9",
        pillHoverBorder: "#cbd5e1",
        label: "#64748b",
        value: "#0f172a",
        icon: "#4d7c2c",
      };

  const scrollbarStyles = {
    scrollbarColor: isDark ? "rgba(148, 163, 184, 0.4) transparent" : "rgba(0,0,0,0.2) transparent",
    "&::-webkit-scrollbar": { width: 8, height: 8 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      background: isDark ? "rgba(148, 163, 184, 0.35)" : "rgba(0,0,0,0.18)",
      borderRadius: 4,
      border: "2px solid transparent",
      backgroundClip: "padding-box",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: isDark ? "rgba(148, 163, 184, 0.5)" : "rgba(0,0,0,0.28)",
      backgroundClip: "padding-box",
    },
  };

  const styles = {
    root: {
      flexGrow: 1,
      height: "60vh",
      overflow: "auto",
      padding: "2px 4px 4px",
      scrollBehavior: "smooth",
      ...scrollbarStyles,
    },
    snapshot: {
      color: assetInfoColors.label,
      overflow: "visible",
      padding: theme.spacing(0.5, 1),
      textAlign: "left",
      backgroundColor: assetInfoColors.containerBg,
      borderRadius: "8px",
      border: `1px solid ${assetInfoColors.containerBorder}`,
      boxShadow: isDark ? "0 1px 4px rgba(0,0,0,0.12)" : "0 1px 3px rgba(15, 23, 42, 0.06)",
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "4px 6px",
      minHeight: 36,
      "& .row-segment": {
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        padding: "2px 5px",
        flexShrink: 0,
        whiteSpace: "nowrap",
        minWidth: 0,
        borderRadius: 9999,
        border: `1px solid ${assetInfoColors.pillBorder}`,
        backgroundColor: assetInfoColors.pillBg,
        transition: "background-color 0.15s ease, border-color 0.15s ease",
        "&:hover": {
          backgroundColor: assetInfoColors.pillHoverBg,
          borderColor: assetInfoColors.pillHoverBorder,
        },
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
        },
      },
      "& .row-label": {
        color: assetInfoColors.label,
        fontSize: "0.65rem",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        flexShrink: 0,
        opacity: 0.95,
        "&::after": { content: '": "' },
      },
      "& .row-value": {
        color: assetInfoColors.value,
        fontSize: "0.65rem",
        fontWeight: isDark ? 500 : 600,
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: 260,
        minWidth: 0,
      },
    },
    infoCard: {
      backgroundColor: bgCard,
      borderRadius: "6px",
      padding: "4px 8px",
      minHeight: "auto",
      height: "100%",
      boxShadow: isDark
        ? "0 1px 2px rgba(0,0,0,0.15)"
        : "0 1px 2px rgba(0,0,0,0.04)",
      border: `1px solid ${borderColor}`,
      transition: "border-color 0.15s ease, box-shadow 0.15s ease",
      "&:hover": {
        borderColor: greenColor,
        boxShadow: isDark
          ? `0 0 0 1px ${greenColor}25`
          : `0 0 0 1px ${greenColor}30`,
      },
      "@media (prefers-reduced-motion: reduce)": {
        transition: "none",
      },
    },
    infoLabel: {
      color: textSecondary,
      fontSize: "0.6rem",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.35px",
      marginBottom: "1px",
      display: "flex",
      alignItems: "center",
      gap: "4px",
      lineHeight: 1.2,
    },
    infoValue: {
      color: textPrimary,
      fontSize: "0.75rem",
      fontWeight: 500,
      lineHeight: 1.25,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    infoValueWrap: {
      color: textPrimary,
      fontSize: "0.75rem",
      fontWeight: 500,
      lineHeight: 1.3,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
    statusChip: {
      fontWeight: 600,
      fontSize: "0.65rem",
      borderRadius: "4px",
      height: 18,
      "& .MuiChip-label": { px: 0.5 },
    },
    sectionTitle: {
      textAlign: "left",
      marginBottom: theme.spacing(0.5),
      fontWeight: 700,
      color: isDark ? greenColor : navyColor,
      fontSize: "0.875rem",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      letterSpacing: isDark ? "normal" : "0.01em",
      "&::before": {
        content: '""',
        width: "2px",
        height: "14px",
        backgroundColor: isDark ? greenColor : "#4d7c2c",
        borderRadius: "2px",
      },
    },
    iconStyle: {
      fontSize: "0.7rem",
      color: assetInfoColors.icon,
    },
    treeContainer: {
      backgroundColor: isDark ? bgContainer : "#f8fafc",
      borderRadius: "8px",
      border: `1px solid ${isDark ? "rgba(160, 196, 109, 0.2)" : "#e2e8f0"}`,
      boxShadow: isDark
        ? "0 1px 4px rgba(0,0,0,0.12)"
        : "0 1px 3px rgba(15, 23, 42, 0.05)",
      padding: theme.spacing(0.5, 0.65),
      maxHeight: "none",
      overflow: "visible",
      textAlign: "left",
    },
  };

  const {
    assetData = {},
    facilities,
    eventTypesMap = {},
    isInventorySnapshot,
    treeView,
  } = props;
  const {
    classificationMap = {},
    customMap,
    facilityId,
    lastEvent = "None provided",
    latitude,
    longitude,
    propertiesMap = {},
    quantity = {},
    tag = "None provided",
    timeCreatedUser,
    timeNeeded,
    zone,
  } = assetData;
  const { description = "None provided", category = "None provided" } =
    propertiesMap;
  const { quantityCurrent } = quantity;

  const InfoCard = ({ icon, label, value, fullWidth = false, statusColor = null, isNumeric = false }) => (
    <Grid size={{ xs: 6, sm: fullWidth ? 12 : 4 }}>
      <Box sx={styles.infoCard} role="group" aria-label={label}>
        <Typography sx={styles.infoLabel}>
          {icon}
          {label}
        </Typography>
        {statusColor ? (
          <Chip
            label={value}
            size="small"
            sx={{
              ...styles.statusChip,
              backgroundColor: statusColor + "15",
              color: statusColor,
              border: `1px solid ${statusColor}40`,
            }}
          />
        ) : (
          <Typography
            sx={{
              ...(fullWidth ? styles.infoValueWrap : styles.infoValue),
              ...(isNumeric ? { textAlign: "right", fontVariantNumeric: "tabular-nums" } : {}),
            }}
            title={value || "–"}
          >
            {value || "–"}
          </Typography>
        )}
      </Box>
    </Grid>
  );

  const [hierarchyLoading, setHierarchyLoading] = useState(true);

  useEffect(() => {
    if (treeView?.complete) {
      setHierarchyLoading(false);
    }
  }, [treeView]);

  const hasHierarchyData = treeView && treeView.id;

  return (
    <Box sx={styles.root}>
      {/* ── Asset / Inventory Information ── */}
      <Typography component="h1" sx={styles.sectionTitle} variant="h5">
        {isInventorySnapshot ? "Inventory" : "Asset"} Information
      </Typography>
      <Paper sx={styles.snapshot} elevation={0} role="region" aria-label={`${isInventorySnapshot ? "Inventory" : "Asset"} information`}>
        <Box className="row-segment">
          <LocalOfferIcon sx={styles.iconStyle} aria-hidden />
          <Typography component="span" className="row-label">{isInventorySnapshot ? "Tag" : "Asset Tag"}</Typography>
          <Tooltip title={tag || ""} placement="top" enterDelay={400} arrow>
            <Typography component="span" className="row-value">{tag || "–"}</Typography>
          </Tooltip>
        </Box>
        <Box className="row-segment">
          <DescriptionIcon sx={styles.iconStyle} aria-hidden />
          <Typography component="span" className="row-label">Description</Typography>
          <Tooltip title={description || ""} placement="top" enterDelay={400} arrow>
            <Typography component="span" className="row-value">{description || "–"}</Typography>
          </Tooltip>
        </Box>
        <Box className="row-segment">
          <CategoryIcon sx={styles.iconStyle} aria-hidden />
          <Typography component="span" className="row-label">Category</Typography>
          <Typography component="span" className="row-value">{category || "–"}</Typography>
        </Box>
        <Box className="row-segment">
          <EventIcon sx={styles.iconStyle} aria-hidden />
          <Typography component="span" className="row-label">Status</Typography>
          {eventTypesMap[lastEvent]?.color ? (
            <Chip
              label={lastEvent}
              size="small"
              sx={{
                ...styles.statusChip,
                backgroundColor: eventTypesMap[lastEvent].color + "15",
                color: eventTypesMap[lastEvent].color,
                border: `1px solid ${eventTypesMap[lastEvent].color}40`,
              }}
            />
          ) : (
            <Typography component="span" className="row-value">{lastEvent || "–"}</Typography>
          )}
        </Box>
        <Box className="row-segment">
          <LocationOnIcon sx={styles.iconStyle} aria-hidden />
          <Typography component="span" className="row-label">Facility</Typography>
          <Tooltip title={latitude && longitude && (latitude !== 0 || longitude !== 0) ? getFacilityNameByRadius(latitude, longitude, facilities) : ""} placement="top" enterDelay={400} arrow>
            <Typography component="span" className="row-value">
              {latitude && longitude && (latitude !== 0 || longitude !== 0) ? getFacilityNameByRadius(latitude, longitude, facilities) : "–"}
            </Typography>
          </Tooltip>
        </Box>
        {timeCreatedUser ? (
          <>
            <Box className="row-segment">
              <EventIcon sx={styles.iconStyle} aria-hidden />
              <Typography component="span" className="row-label">Created</Typography>
              <Typography component="span" className="row-value">
                {moment(timeCreatedUser, "YYYYMMDDHHmmss").format("MM/DD/YYYY") || "–"}
              </Typography>
            </Box>
          </>
        ) : null}
        {timeNeeded ? (
          <>
            <Box className="row-segment">
              <EventIcon sx={styles.iconStyle} aria-hidden />
              <Typography component="span" className="row-label">Needed</Typography>
              <Typography component="span" className="row-value">
                {moment(timeNeeded, "YYYYMMDDHHmmss").format("MM/DD/YYYY") || "–"}
              </Typography>
            </Box>
          </>
        ) : null}
        {zone?.binLocation ? (
          <>
            <Box className="row-segment">
              <LocationOnIcon sx={styles.iconStyle} aria-hidden />
              <Typography component="span" className="row-label">Bin</Typography>
              <Typography component="span" className="row-value">{zone.binLocation}</Typography>
            </Box>
          </>
        ) : null}
        {customMap && !_.isEmpty(customMap)
          ? Object.keys(customMap)
              .sort((a, b) => (a === b ? 0 : a.localeCompare(b)))
              .filter((key) => customMap[key])
              .map((key) => (
                <Box key={key}>
                  <Box className="row-segment">
                    <DescriptionIcon sx={styles.iconStyle} aria-hidden />
                    <Typography component="span" className="row-label">{key === "note" ? "Notes" : _.startCase(key)}</Typography>
                    <Tooltip title={customMap[key]} placement="top" enterDelay={400} arrow>
                      <Typography component="span" className="row-value">{customMap[key]}</Typography>
                    </Tooltip>
                  </Box>
                </Box>
              ))
          : null}
        {propertiesMap?.note && typeof propertiesMap.note === "string" ? (
          <>
            <Box className="row-segment">
              <DescriptionIcon sx={styles.iconStyle} aria-hidden />
              <Typography component="span" className="row-label">Notes</Typography>
              <Tooltip title={propertiesMap.note} placement="top" enterDelay={400} arrow>
                <Typography component="span" className="row-value">{propertiesMap.note}</Typography>
              </Tooltip>
            </Box>
          </>
        ) : null}
        {Object.keys(classificationMap || {}).map((item) => (
          <Box key={item}>
            <Box className="row-segment">
              <CategoryIcon sx={styles.iconStyle} aria-hidden />
              <Typography component="span" className="row-label">{item}</Typography>
              <Typography component="span" className="row-value">{classificationMap[item]}</Typography>
            </Box>
          </Box>
        ))}
        {isInventorySnapshot && quantityCurrent != null ? (
          <>
            <Box className="row-segment">
              <InventoryIcon sx={styles.iconStyle} aria-hidden />
              <Typography component="span" className="row-label">Qty</Typography>
              <Typography component="span" className="row-value" sx={{ fontVariantNumeric: "tabular-nums" }}>{quantityCurrent}</Typography>
            </Box>
          </>
        ) : null}
      </Paper>

      {/* ── Hierarchy Tree ── */}
      <Box sx={{ mt: 2.5 }}>
        <Typography component="h2" sx={{ ...styles.sectionTitle, mb: 0.5, fontSize: "0.95rem" }} variant="h5">
          <AccountTreeIcon sx={{ color: greenColor, fontSize: "1rem" }} />
          Hierarchy
        </Typography>

        <Paper sx={styles.treeContainer} elevation={0} role="region" aria-label="Asset hierarchy">
          {hierarchyLoading ? (
            <Box sx={{ px: 1, py: 0.5 }}>
              {[1, 2, 3].map((i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                  <Skeleton
                    variant="circular"
                    width={20}
                    height={20}
                    sx={{ bgcolor: isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(0,0,0,0.08)", flexShrink: 0 }}
                  />
                  <Skeleton
                    variant="rounded"
                    height={28}
                    sx={{
                      bgcolor: isDark ? "rgba(148, 163, 184, 0.12)" : "rgba(0,0,0,0.06)",
                      borderRadius: "6px",
                      flex: 1,
                      maxWidth: i === 1 ? "85%" : i === 2 ? "65%" : "50%",
                    }}
                  />
                </Box>
              ))}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                <CircularProgress size={20} sx={{ color: greenColor }} />
              </Box>
            </Box>
          ) : !hasHierarchyData ? (
            <Box
              role="status"
              aria-label="No hierarchy data"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 120,
                gap: 1,
                py: 2,
              }}
            >
              <AccountTreeIcon sx={{ fontSize: "2rem", color: isDark ? "rgba(148, 163, 184, 0.3)" : "rgba(100, 116, 139, 0.3)" }} />
              <Typography variant="body2" sx={{ color: textSecondary, fontWeight: 600 }}>
                No hierarchy data available
              </Typography>
              <Typography variant="caption" sx={{ color: textSecondary, opacity: 0.8 }}>
                This asset has no parent or child relationships.
              </Typography>
            </Box>
          ) : (
            <RecursiveTreeView data={treeView} isDark={isDark} />
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default Overview;
